import { useRef, useEffect } from 'react'
import type { TreeNode as TreeNodeType } from 'src/types/global'

import './TreeNode.css'

interface TreeNodeProps {
  node: TreeNodeType
  selectedId?: string
  onSelect: (node: TreeNodeType) => void
  expandedIds?: Set<string>
}

const iconMap: Record<string, string | undefined> = {
  group: undefined,
  source: 'icon-home-stroke',
  collection: 'icon-pile-stroke',
  dataset: 'icon-table'
}

export default function TreeNode({
  node,
  selectedId,
  onSelect,
  expandedIds
}: TreeNodeProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const isLeaf = !node.children || node.children.length === 0
  const isSelected = node.id === selectedId
  const icon = iconMap[node.type]

  useEffect(() => {
    if (
      expandedIds?.has(node.id) &&
      detailsRef.current &&
      !detailsRef.current.open
    ) {
      detailsRef.current.open = true
    }
  }, [expandedIds, node.id])

  if (isLeaf) {
    return (
      <li
        className={`tree-leaf ${isSelected ? 'selected' : ''}`}
        onClick={() => onSelect(node)}
      >
        {icon && <span className={`icon ${icon}`} />}
        <span title={node.name} className="ellipsis">
          {node.name}
        </span>
      </li>
    )
  }

  return (
    <li className="tree-branch">
      <details className="tree-node" ref={detailsRef}>
        <summary>
          <span className="icon icon-angle-right" />
          {icon && <span className={`icon ${icon}`} />}
          <span title={node.name}>{node.name}</span>
        </summary>
        <ul>
          {node.children?.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              expandedIds={expandedIds}
            />
          ))}
        </ul>
      </details>
    </li>
  )
}
